<template>
    <DropdownMenu
        :items="userMenuActions"
        :position-x-offset="-8"
        :position-y-offset="8"
        :width="300"
        :has-no-padding="true"
        :has-shadow="true"
        dropdown-class="top-bar-user-menu"
    >
        <template #activator>
            <ActionIconButton
                is-rounded
                :icon="profileImage ? '' : 'fa-solid fa-user'"
                :image-src="profileImage"
                :title="profileName || $t('User settings')"
                :aria-label="profileName || $t('User settings')"
                class="button-border"
                :style-type="'primary-brand-soft'"
                icon-class="icon-color"
                aria-haspopup="menu"
                size="xl"
            />
        </template>
    </DropdownMenu>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ActionIconButton from "@/components/buttons/ActionIconButton.vue";
import DropdownMenu from "@/components/dropdowns/dropdown-menu/DropdownMenu.vue";
import type { DropdownMenuItemData } from "@/types/dropdowns";

export default defineComponent({
    components: {
        ActionIconButton,
        DropdownMenu,
    },
    name: "UserSettingsButton",
    emits: ["select-language", "open-config-modal", "open-logout-modal"],
    props: {
        profileName: {
            type: String,
            default: "",
        },
        profileImage: {
            type: String,
            default: "",
        },
        username: {
            type: String,
            required: true,
        },
        canAdmin: {
            type: Boolean,
            default: false,
        },
        canConfigDataSpace: {
            type: Boolean,
            default: false,
        },
        renderLanguage: {
            type: Function,
            required: true,
        },
        getLanguage: {
            type: String,
        },
    },
    computed: {
        userMenuActions(): DropdownMenuItemData[] {
            const currentLang = this.$locale.value || this.getLanguage;
            const actions: DropdownMenuItemData[] = [
                {
                    actionType: "link",
                    text: this.$t("My profile"),
                    icon: "fas fa-user-circle",
                    type: "icon",
                    to: "/user/@" + encodeURIComponent(this.username),
                },
                {
                    actionType: "action",
                    text: this.$t("Language") + ": " + this.renderLanguage(currentLang),
                    icon: "fas fa-language",
                    type: "icon",
                    hasSeparator: true,
                    callback: () => this.$emit("select-language"),
                },
            ];

            actions.push({
                actionType: "link",
                text: this.$t("Account settings"),
                icon: "fas fa-cog",
                type: "icon",
                hasSeparator: true,
                to: "/account-settings",
            });

            if (this.canAdmin) {
                actions.push({
                    actionType: "link",
                    text: this.$t("Administration and moderation"),
                    icon: "fas fa-hammer",
                    type: "icon",
                    hasSeparator: true,
                    to: "/admin",
                });
            }

            if (this.canConfigDataSpace) {
                actions.push(
                    {
                        actionType: "link",
                        text: this.$t("Data space configuration"),
                        icon: "fas fa-sliders-h",
                        type: "icon",
                        hasSeparator: true,
                        to: "/admin/data-space",
                    },
                    {
                        actionType: "action",
                        text: this.$t("Open configuration modal"),
                        icon: "fas fa-external-link-alt",
                        type: "icon",
                        callback: () => this.$emit("open-config-modal"),
                    },
                );
            }

            actions.push({
                actionType: "action",
                text: this.$t("Log out"),
                icon: "fas fa-sign-out-alt",
                type: "danger-icon",
                hasSeparator: true,
                callback: () => this.$emit("open-logout-modal"),
            });

            return actions;
        },
    },
});
</script>
